import { useContext, useEffect } from 'react'
import { UserContext, UserContextType } from '../../UserContext'
import ElectronIPC from '../../services/ElectronIPC'
import { persistent } from '../../services/LocalStorage'
import useMediaPermissionActions from '../../functionalities/mediaPermissions/useMediaPermissionActions'
import { IconCircle } from '@/components/CircleIcon'
import CameraIcon from '@/CustomIcons/Gradient/CameraIcon'
import { Button } from '@/UIComponents2/Button'
import ScreenRecordingIcon from '@/CustomIcons/Gradient/ScreenRecordingIcon'
import MicrophoneIcon from '@/CustomIcons/Gradient/MicrophoneIcon'
import EnabledCheckMark from './EnabledCheckMark'
import AlertIcon from '@/CustomIcons/AlertIcon'
import AccessibilityIcon from '@/CustomIcons/Gradient/AccessibilityIcon'
import { DialogBody, DialogSubtitle, DialogTitle } from '@/components/Dialog'
import DialogActionButtonsSection from '@/dialogs/components/DialogActionButtonsSection'
import { useTranslation } from 'react-i18next'
import RightArrowIcon from '@/CustomIcons/RightArrowIcon'

/**
 * start permission checklist
 *
 * if muse detects we don't have necessary permissions (like if it's the first time
 * user opens app), we will show the permissions checklist popup as soon as they login.
 * first time user will be onboarded via welcome to muse page, where we tell them
 * "here's what you need to get started" and permissions are on the list.
 * if it's on choose initial mode page it should come before any popups.
 * as soon as permission checklist shows up we will start asking for request, one by one,
 * so they user can give us permissions and restart app .
 */

function EnableSystemAccessPage() {
    const { t } = useTranslation()
    const { state } = useContext(UserContext) as UserContextType

    const { askForInitialMediaAccess, openPrivacyPreferencesSettings, closeMediaPermissionsChecklist } =
        useMediaPermissionActions()

    useEffect(() => {
        // nasty: timeout is here so that calls to `getCurrentMediaPermissions()`
        // don't "overlap".
        setTimeout(() => {
            askForInitialMediaAccess()
        }, 1500)
    }, [])

    const areAllPermissionsGood = (() => {
        for (let k of Object.keys(state.mediaPermissions)) {
            // @ts-ignore
            let p = state.mediaPermissions[k]
            if (p === false) {
                return false
            }
        }
        return true
    })()

    const onSkip = () => {
        persistent.set('donotshow.MediaPermissionsChecklistModal', true)
        closeMediaPermissionsChecklist()
    }

    const onContinue = () => {
        persistent.set('donotshow.MediaPermissionsChecklistModal', true)
        closeMediaPermissionsChecklist()
    }

    const relaunchApp = () => {
        ElectronIPC.quitApp({ immediate: true, relaunch: true })
    }

    return (
        <div className="flex flex-col w-full h-full justify-between">
            <DialogBody className="flex flex-col gap-6 px-0">
                <div className="w-full flex flex-col gap-[40px]">
                    <div className="w-full flex flex-col gap-[8px] px-[48px]">
                        <DialogTitle className="pb-1">Enable System Access</DialogTitle>
                        <DialogSubtitle>
                            Muse requires your permission to access system capabilities to work properly.
                        </DialogSubtitle>
                    </div>

                    <div className="flex flex-col gap-[20px]">
                        <PermissionItem
                            Icon={CameraIcon}
                            title="Camera"
                            description="Muse needs Camera access so you can share your webcam."
                            onButtonClick={() => openPrivacyPreferencesSettings('camera')}
                            status={state.mediaPermissions.video}
                        />
                        <Separator />
                        <PermissionItem
                            Icon={ScreenRecordingIcon}
                            title="Screen Recording"
                            description="Muse needs Screen Recording access so you can record sessions and share your screen."
                            onButtonClick={() => openPrivacyPreferencesSettings('screen')}
                            status={state.mediaPermissions.desktop}
                        />
                        <Separator />
                        <PermissionItem
                            Icon={MicrophoneIcon}
                            title="Microphone"
                            description="Muse needs Microphone access so others can hear you speak."
                            onButtonClick={() => openPrivacyPreferencesSettings('microphone')}
                            status={state.mediaPermissions.audio}
                        />
                        <Separator />
                        <PermissionItem
                            Icon={AccessibilityIcon}
                            title="Accessibility"
                            description="Muse needs Accessibility access so your mouse and keyboard can be controlled remotely."
                            onButtonClick={() => openPrivacyPreferencesSettings('accessibility')}
                            status={state.mediaPermissions.accessibility}
                        />
                    </div>
                </div>
            </DialogBody>

            {areAllPermissionsGood ? (
                <DialogActionButtonsSection
                    rightButtonText={
                        <div className="w-full h-full relative">
                            {t('Continue')}
                            <RightArrowIcon className="absolute right-0" />
                        </div>
                    }
                    onRightButtonClicked={onContinue}
                    fullWidthButton={false}
                />
            ) : (
                <DialogActionButtonsSection
                    leftButtonText={t('Set this up later')}
                    rightButtonText={
                        <div className="w-full h-full relative">
                            {t('Save & restart app')}
                            <RightArrowIcon className="absolute right-0" />
                        </div>
                    }
                    onLeftButtonClicked={onSkip}
                    onRightButtonClicked={relaunchApp}
                />
            )}
        </div>
    )
}

interface PermissionItemProps {
    Icon: React.ComponentType<{ style?: React.CSSProperties }>
    title: string
    description: string
    onButtonClick: () => void
    status: boolean | null
}

function PermissionItem({ Icon, title, description, onButtonClick, status }: PermissionItemProps) {
    return (
        <div className="flex gap-[16px] items-center px-[46px]">
            <OuterCircle>
                <IconCircle className="w-[56px] h-[56px]" glowColor={status ? 'muse-success' : 'muse-danger'}>
                    <Icon style={{ fontSize: '1.6rem' }} />
                </IconCircle>
            </OuterCircle>
            <div className="flex flex-col gap-[8px] flex-1 pr-[40px]">
                <div className="t-base-medium">{title}</div>
                <div className="opacity-70">{description}</div>
            </div>
            <Button
                variant="rectangle"
                className="rounded-full px-[18px] py-[9px]"
                onClick={onButtonClick}
                disabled={!!status}
            >
                {status ? (
                    <>
                        <EnabledCheckMark /> <span className="text-muse-success">Enabled</span>
                    </>
                ) : (
                    <>
                        <AlertIcon className="text-muse-warning" /> <span className="text-muse-warning">Enable</span>
                    </>
                )}
            </Button>
        </div>
    )
}

function OuterCircle({ children }: { children: React.ReactNode }) {
    return (
        <div className="w-[70px] h-[70px] rounded-full border-[rgba(255,255,255,0.02)] border-b-[rgba(255,255,255,0)] border-t-[rgba(255,255,255,0.05)] border-[1px] bg-[rgba(255,255,255,0.01)] flex items-center justify-center">
            {children}
        </div>
    )
}

function Separator() {
    return <div className="h-[1px] bg-[rgba(255,255,255,0.03)] w-full" />
}

export default EnableSystemAccessPage
